import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import VideoCard from "@/components/feature/VideoCard";
import ScrollTop from "@/components/feature/ScrollTop";
import { useSeo } from "@/hooks/useSeo";
import { videos, categories, hotTopics } from "@/mocks/videos";

type Filter = "all" | "hot" | "newest";

export default function Category() {
  const { slug = "gossip" } = useParams();
  const [filter, setFilter] = useState<Filter>("all");

  const category = categories.find((c) => c.slug === slug) ?? categories[0];

  const list = useMemo(() => {
    const base = videos.filter((v) => v.category === category.slug);
    if (filter === "hot") return base.filter((v) => v.hot);
    if (filter === "newest") return [...base].reverse();
    return base;
  }, [category.slug, filter]);

  useSeo({
    title: `${category.name} - 吃瓜视频网`,
    description: `吃瓜视频网${category.name}栏目，收录全网${category.name}相关视频与资讯，每日更新圈内动态、幕后花絮与高能片段，一站看全。`,
    keywords: `吃瓜视频网,${category.name},娱乐圈,${category.name}视频`,
  });

  const filters: { key: Filter; label: string }[] = [
    { key: "all", label: "全部" },
    { key: "hot", label: "热门" },
    { key: "newest", label: "最新" },
  ];

  return (
    <div className="min-h-screen">
      <Header />

      <main className="mx-auto max-w-7xl px-4 md:px-6 pt-6">
        {/* breadcrumb */}
        <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-500 mb-5">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">{category.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {/* main */}
          <div className="lg:col-span-2">
            <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
              <div>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500/10 text-primary-700 text-xs font-bold">
                  <i className={category.icon}></i>{category.name}
                </span>
                <h1 className="mt-3 font-heading text-2xl md:text-3xl text-foreground-950">{category.name}</h1>
                <p className="mt-1 text-sm text-foreground-500">{category.desc}</p>
              </div>
              <div className="flex items-center gap-1 bg-background-100 border border-background-200 rounded-full p-1">
                {filters.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                      filter === f.key ? "bg-primary-500 text-background-50 font-bold" : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </header>

            {list.length === 0 ? (
              <div className="bg-background-100 border border-background-200 rounded-lg p-10 text-center text-foreground-500">
                该栏目暂无内容，欢迎稍后再来看看。
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                {list.map((v) => (
                  <VideoCard key={v.slug} video={v} />
                ))}
              </div>
            )}
          </div>

          {/* sidebar */}
          <aside className="space-y-6">
            <div className="bg-background-100 border border-background-200 rounded-lg p-5">
              <h2 className="text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-fire-line text-accent-500"></i>热门搜索
              </h2>
              <div className="flex flex-wrap gap-2">
                {hotTopics.map((t) => (
                  <Link
                    key={t}
                    to={`/search?q=${encodeURIComponent(t)}`}
                    className="px-3 py-1.5 rounded-full bg-background-50 border border-background-200 text-xs text-foreground-600 hover:border-primary-300 hover:text-primary-600 whitespace-nowrap"
                  >
                    {t}
                  </Link>
                ))}
              </div>
            </div>

            <div className="bg-background-100 border border-background-200 rounded-lg p-5">
              <h2 className="text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-layout-grid-line text-primary-500"></i>全部栏目
              </h2>
              <ul className="space-y-2">
                {categories.map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={`/category/${c.slug}`}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm ${
                        c.slug === category.slug
                          ? "bg-primary-500 text-background-50 font-bold"
                          : "text-foreground-700 hover:bg-background-50"
                      }`}
                    >
                      <i className={c.icon}></i>{c.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </main>

      <Footer />
      <ScrollTop />
    </div>
  );
}